"use client"

import { useEffect, useState, useRef } from "react"
import { Form } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { Search, Ticket } from "lucide-react"
import { Link } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { fetchTickets, clearTickets } from "../../redux/slices/ticketsSlice"
import { useAuth } from "../../contexts/auth-context"
import { PlusButton } from "../../components/ui/plus-button"

export default function TicketsPage() {
  const dispatch = useAppDispatch()
  const { tickets = [], isLoading } = useAppSelector((state) => state.tickets)
  const { user } = useAuth()
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState("")
  const [activeTab, setActiveTab] = useState("open")
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (user) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      dispatch(clearTickets())

      abortControllerRef.current = new AbortController()

      dispatch(
        fetchTickets({
          userId: user.id,
          signal: abortControllerRef.current.signal,
        }),
      )
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [dispatch, user])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const filteredTickets = tickets.filter(
    (ticket) =>
      ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.type.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const openTickets = tickets.filter((ticket) => ticket.status === "open")
  const closedTickets = tickets.filter((ticket) => ticket.status === "closed")

  const displayedTickets =
    activeTab === "open"
      ? filteredTickets.filter((ticket) => ticket.status === "open")
      : activeTab === "closed"
        ? filteredTickets.filter((ticket) => ticket.status === "closed")
        : filteredTickets

  if (isLoading && tickets.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-white" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4" style={{ height: "64px" }}>
        <div className="d-flex align-items-center gap-3">
          <h1 className="fs-2 fw-bold mb-0 text-white">{t("tickets")}</h1>
          <Ticket className="text-white" size={32} />
        </div>
        <PlusButton variant="floating" to="/dashboard/tickets/new" />
      </div>

      <div className="mb-4">
        <div className="position-relative">
          <div
            className="d-flex align-items-center gap-3 p-4 rounded-4"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Search className="text-white opacity-75" size={24} />
            <Form.Control
              type="search"
              placeholder={t("searchTickets")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-0 text-white fs-5 p-0"
              style={{
                outline: "none",
                boxShadow: "none",
                color: "white",
                "--bs-form-control-color": "white",
              }}
            />
            <style jsx>{`
              .form-control::placeholder {
                color: rgba(255, 255, 255, 0.7) !important;
                opacity: 1;
              }
            `}</style>
          </div>
        </div>
      </div>

      <div className="d-flex gap-3 mb-4">
        <button
          className={`btn rounded-pill px-4 py-2 fw-bold d-flex align-items-center gap-2 filter-tab ${
            activeTab === "open" ? "selected bg-white text-primary" : "text-white border-0"
          }`}
          style={{
            background: activeTab === "open" ? "white" : "rgba(255, 255, 255, 0.1)",
            backdropFilter: activeTab === "open" ? "none" : "blur(10px)",
            border: activeTab === "open" ? "none" : "1px solid rgba(255, 255, 255, 0.2)",
          }}
          onClick={() => setActiveTab("open")}
        >
          {t("open")} ({openTickets.length})
        </button>
        <button
          className={`btn rounded-pill px-4 py-2 fw-bold d-flex align-items-center gap-2 filter-tab ${
            activeTab === "closed" ? "selected bg-white text-primary" : "text-white border-0"
          }`}
          style={{
            background: activeTab === "closed" ? "white" : "rgba(255, 255, 255, 0.1)",
            backdropFilter: activeTab === "closed" ? "none" : "blur(10px)",
            border: activeTab === "closed" ? "none" : "1px solid rgba(255, 255, 255, 0.2)",
          }}
          onClick={() => setActiveTab("closed")}
        >
          {t("closed")} ({closedTickets.length})
        </button>
        <button
          className={`btn rounded-pill px-4 py-2 fw-bold d-flex align-items-center gap-2 filter-tab ${
            activeTab === "all" ? "selected bg-white text-primary" : "text-white border-0"
          }`}
          style={{
            background: activeTab === "all" ? "white" : "rgba(255, 255, 255, 0.1)",
            backdropFilter: activeTab === "all" ? "none" : "blur(10px)",
            border: activeTab === "all" ? "none" : "1px solid rgba(255, 255, 255, 0.2)",
          }}
          onClick={() => setActiveTab("all")}
        >
          {t("all")} ({tickets.length})
        </button>
      </div>

      <div className="d-flex flex-column gap-3">
        {displayedTickets.length === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center p-5 rounded-4 text-white"
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <p className="mb-0 opacity-75">{t("noTicketsFound")}</p>
          </div>
        ) : (
          displayedTickets.map((ticket) => (
            <Link key={ticket.id} to={`/dashboard/tickets/${ticket.id}`} className="text-decoration-none">
              <div
                className="card-gradient p-4 rounded-4 position-relative"
                style={{
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)"
                }}
              >
                <div className="d-flex align-items-start justify-content-between">
                  <div className="d-flex align-items-start gap-3 flex-grow-1">
                    <div
                      className={`rounded-circle mt-1 ${ticket.status === "open" ? "bg-warning" : "bg-success"}`}
                      style={{ width: "16px", height: "16px", minWidth: "16px" }}
                    ></div>

                    <div className="flex-grow-1">
                      <h3 className="text-white mb-2 fs-4 fw-medium">{ticket.title}</h3>
                      {ticket.description && <p className="text-white opacity-75 mb-2 fs-6">{ticket.description}</p>}
                      <div className="text-white opacity-50 small">
                        {ticket.comments?.length || 0} {t("comments")}
                      </div>
                    </div>
                  </div>

                  <div className="text-white text-end ms-3" style={{ minWidth: "100px" }}>
                    <div className="fs-5 fw-medium mb-1">
                      {new Date(ticket.createdAt).toLocaleTimeString("el-GR", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </div>
                    <div className="opacity-75">
                      {new Date(ticket.createdAt).toLocaleDateString("el-GR", {
                        month: "numeric",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  )
}
